/**
 * Syncs mobile sticky bar quantity + add-to-cart with the main product form.
 * Bottom offset follows visualViewport so the bar stays flush when iOS browser chrome shows/hides.
 */
(function () {
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  let bottomInsetRaf = null;

  function portalStickyBarToBody(stickyBar) {
    if (!stickyBar || stickyBar.parentElement === document.body) return;
    document.body.appendChild(stickyBar);
  }

  function updateBottomInset() {
    if (!MOBILE_QUERY.matches) {
      document.documentElement.style.removeProperty('--sticky-bar-bottom');
      return;
    }

    const vv = window.visualViewport;
    if (!vv) {
      document.documentElement.style.removeProperty('--sticky-bar-bottom');
      return;
    }

    // Gap between layout viewport bottom and visual viewport bottom (browser toolbars).
    const inset = Math.max(0, window.innerHeight - vv.offsetTop - vv.height);
    document.documentElement.style.setProperty('--sticky-bar-bottom', `${inset}px`);
  }

  function scheduleBottomInsetUpdate() {
    if (bottomInsetRaf) return;
    bottomInsetRaf = requestAnimationFrame(() => {
      bottomInsetRaf = null;
      updateBottomInset();
    });
  }

  class StickyBarController {
    constructor(stickyBar) {
      this.stickyBar = stickyBar;
      this.sectionId = document.querySelector('product-info')?.dataset.section;
      this.syncingQty = false;
      this.submitObserver = null;
      this.resizeObserver = null;

      portalStickyBarToBody(stickyBar);

      if (!this.sectionId) return;

      this.cacheElements();
      if (!this.mainQty || !this.stickyQty || !this.mainSubmit || !this.stickySubmit) return;

      this.bindQuantitySync();
      this.bindSubmit();
      this.observeMainSubmit();
      this.observeBarHeight();
      this.syncQuantityFromMain();
      this.syncQuantityAttributesFromMain();
      this.mirrorSubmitState();
      this.updateReservedSpace();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, () => this.onProductFormUpdate());
        subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.onProductFormUpdate());
      }

      document.querySelector('product-info')?.addEventListener('product-info:loaded', () => {
        this.cacheElements();
        this.onProductFormUpdate();
        this.observeMainSubmit();
      });
    }

    cacheElements() {
      this.mainQty = document.getElementById(`Quantity-${this.sectionId}`);
      this.mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.stickyQty = this.stickyBar.querySelector('[data-sticky-quantity]');
      this.stickySubmit = this.stickyBar.querySelector('[data-sticky-submit]');
    }

    onProductFormUpdate() {
      this.cacheElements();
      this.syncQuantityFromMain();
      this.syncQuantityAttributesFromMain();
      this.mirrorSubmitState();
      this.updateReservedSpace();
    }

    bindQuantitySync() {
      const onMainQtyChange = () => this.syncQuantity(this.mainQty, this.stickyQty);
      const onStickyQtyChange = () => this.syncQuantity(this.stickyQty, this.mainQty);

      this.mainQty.addEventListener('change', onMainQtyChange);
      this.mainQty.addEventListener('input', onMainQtyChange);
      this.stickyQty.addEventListener('change', onStickyQtyChange);
      this.stickyQty.addEventListener('input', onStickyQtyChange);
    }

    syncQuantity(from, to) {
      if (this.syncingQty || !from || !to || from.value === to.value) return;

      this.syncingQty = true;
      to.value = from.value;
      to.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncingQty = false;
    }

    syncQuantityFromMain() {
      if (!this.mainQty || !this.stickyQty) return;
      this.syncingQty = true;
      this.stickyQty.value = this.mainQty.value;
      this.stickyQty.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncingQty = false;
    }

    syncQuantityAttributesFromMain() {
      if (!this.mainQty || !this.stickyQty) return;

      ['min', 'max', 'step', 'data-min', 'data-max', 'data-cart-quantity'].forEach((attr) => {
        const value = this.mainQty.getAttribute(attr);
        if (value === null) {
          this.stickyQty.removeAttribute(attr);
        } else {
          this.stickyQty.setAttribute(attr, value);
        }
      });

      const stickyQuantityInput = this.stickyQty.closest('quantity-input');
      if (stickyQuantityInput && typeof stickyQuantityInput.validateQtyRules === 'function') {
        stickyQuantityInput.validateQtyRules();
      }
    }

    bindSubmit() {
      this.stickySubmit.addEventListener('click', (event) => {
        event.preventDefault();

        if (
          this.mainSubmit.disabled ||
          this.mainSubmit.getAttribute('aria-disabled') === 'true' ||
          this.mainSubmit.classList.contains('loading')
        ) {
          return;
        }

        this.syncQuantityFromMain();
        this.mainSubmit.click();
      });
    }

    observeMainSubmit() {
      this.submitObserver?.disconnect();

      if (!this.mainSubmit) return;

      this.submitObserver = new MutationObserver(() => this.mirrorSubmitState());
      this.submitObserver.observe(this.mainSubmit, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
      });
    }

    observeBarHeight() {
      this.resizeObserver?.disconnect();

      if (typeof ResizeObserver === 'undefined') return;

      this.resizeObserver = new ResizeObserver((entries) => {
        this.updateReservedSpace(entries[0]);
      });
      this.resizeObserver.observe(this.stickyBar);
    }

    mirrorSubmitState() {
      if (!this.mainSubmit || !this.stickySubmit) return;

      const mainLabel = this.mainSubmit.querySelector('span:not(.sold-out-message)');
      const stickyLabel = this.stickySubmit.querySelector('span');
      if (mainLabel && stickyLabel) {
        stickyLabel.textContent = mainLabel.textContent;
      }

      this.stickySubmit.disabled = this.mainSubmit.disabled;
      if (this.mainSubmit.hasAttribute('aria-disabled')) {
        this.stickySubmit.setAttribute('aria-disabled', this.mainSubmit.getAttribute('aria-disabled'));
      } else {
        this.stickySubmit.removeAttribute('aria-disabled');
      }

      this.stickySubmit.classList.toggle('loading', this.mainSubmit.classList.contains('loading'));

      const mainSpinner = this.mainSubmit.querySelector('.loading__spinner');
      const stickySpinner = this.stickySubmit.querySelector('.loading__spinner');
      if (mainSpinner && stickySpinner) {
        stickySpinner.classList.toggle('hidden', mainSpinner.classList.contains('hidden'));
      }
    }

    updateReservedSpace(entry) {
      if (!MOBILE_QUERY.matches) {
        document.documentElement.style.removeProperty('--sticky-bar-height');
        document.body.classList.remove('has-sticky-bar');
        return;
      }

      let height = 0;
      if (entry && entry.borderBoxSize && entry.borderBoxSize.length) {
        height = entry.borderBoxSize[0].blockSize;
      } else if (entry && entry.contentRect) {
        height = entry.contentRect.height;
      } else {
        height = this.stickyBar.offsetHeight;
      }
      document.documentElement.style.setProperty('--sticky-bar-height', `${Math.round(height)}px`);
      document.body.classList.add('has-sticky-bar');
    }
  }

  function initStickyBars() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo?.dataset.section) return;

    updateBottomInset();

    document.querySelectorAll('[data-sticky-bar]').forEach((stickyBar) => {
      portalStickyBarToBody(stickyBar);
      if (stickyBar.stickyBarController) return;
      stickyBar.stickyBarController = new StickyBarController(stickyBar);
    });
  }

  function tryInit() {
    initStickyBars();
    if (!document.querySelector('[data-sticky-bar]')?.stickyBarController) {
      document.querySelector('product-info')?.addEventListener('product-info:loaded', initStickyBars, { once: true });
    }
  }

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleBottomInsetUpdate);
    window.visualViewport.addEventListener('scroll', scheduleBottomInsetUpdate);
  }

  window.addEventListener('resize', scheduleBottomInsetUpdate);
  window.addEventListener('orientationchange', scheduleBottomInsetUpdate);
  window.addEventListener('scroll', scheduleBottomInsetUpdate, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInit);
  } else {
    tryInit();
  }

  MOBILE_QUERY.addEventListener('change', () => {
    updateBottomInset();
    tryInit();
  });
})();
